import React, { useEffect, useRef, useState } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Document from '@tiptap/extension-document';
import { Markdown } from '@tiptap/markdown';
import TaskList from '@tiptap/extension-task-list';
import TaskItem from '@tiptap/extension-task-item';
import Placeholder from '@tiptap/extension-placeholder';
import classNames from 'classnames';
import { Extension } from '@tiptap/core';

import styles from './TaskItemTiptapEditor.module.css';
import '../TaskItem/prose.css';
import { consoleLog } from '../../utilities/console';

import { cleanMarkdown } from '../../utilities/cleanMarkdown';

type Props = {
  value: string;
  placeholder?: string;
  updateContent: (markdown: string) => void;
  updateIsEditing: (isEditing: boolean) => void;
  autofocus?: boolean | 'start' | 'end' | 'all';
  readOnly?: boolean;
};

const CustomDocument = Document.extend({
  content: 'heading block*',
});

const HeadingSplit = Extension.create({
  name: 'headingSplit',
  addKeyboardShortcuts() {
    return {
      Enter: ({ editor }) => {
        const { state } = editor;
        const { selection } = state;
        const { $from, empty } = selection;

        if (empty && $from.parent.type.name === 'heading') {
          const { schema } = state;
          try {
            const tr = state.tr.split($from.pos, 1, [
              { type: schema.nodes.paragraph },
            ]);
            editor.view.dispatch(tr);
            return true;
          } catch (e) {
            console.error(e);
          }
        }
        return false;
      },
    };
  },
});

export const TaskItemTiptapEditor: React.FC<Props> = ({
  value,
  placeholder,
  updateContent,
  updateIsEditing,
  autofocus = false,
  readOnly = false,
}: Props) => {
  const editorContainerRef = useRef<HTMLDivElement>(null);
  const [isRawMarkdown, setIsRawMarkdown] = useState(false);

  const editor = useEditor({
    editable: !readOnly,
    extensions: [
      HeadingSplit,
      CustomDocument,
      StarterKit.configure({
        document: false,
        trailingNode: {
          node: 'paragraph',
        },
      }),
      Markdown.configure({
        markedOptions: { gfm: true },
      }),
      TaskList,
      TaskItem.configure({
        nested: true,
      }),
      Placeholder.configure({
        placeholder: ({ editor, node, pos }) => {
          const { doc } = editor.state;
          const nodeIndex = doc.resolve(pos).index(0);

          if (node.type.name === 'heading' && nodeIndex === 0) {
            return 'Enter a title..';
          }
          if (node.type.name === 'paragraph' && nodeIndex === 1) {
            return 'Enter details..';
          }

          return '';
        },
      }),
    ],
    content: value,
    contentType: 'markdown',
    autofocus: readOnly ? false : autofocus,
    editorProps: {
      attributes: {
        class: 'prose focus:outline-none',
      },
    },
    onUpdate: ({ editor }) => {
      const rawMarkdown = editor.getMarkdown();
      updateContent(rawMarkdown);
    },
  });

  // Sync editor editable state with readOnly prop
  useEffect(() => {
    if (editor) {
      editor.setEditable(!readOnly);
    }
  }, [editor, readOnly]);

  // Sync value from prop when editor is not focused (external changes)
  useEffect(() => {
    if (!editor) return;
    if (isRawMarkdown) return; // Skip background syncing while typing in raw markdown mode

    if (!editor.isFocused) {
      const currentMarkdown = editor.getMarkdown();
      if (cleanMarkdown(value) !== cleanMarkdown(currentMarkdown)) {
        editor.commands.setContent(value, {
          emitUpdate: false,
          contentType: 'markdown',
        });
      }
    }
  }, [value, editor, isRawMarkdown]);

  // Manage focus states at the container level
  useEffect(() => {
    const editorContainer = editorContainerRef.current;
    if (editorContainer) {
      const focusoutHandler = (event: FocusEvent) => {
        if (
          editorContainer &&
          !editorContainer.contains(event.relatedTarget as Node)
        ) {
          updateIsEditing(false);
          consoleLog('Tiptap focusout detected');

          if (editor && !isRawMarkdown) {
            const rawMarkdown = editor.getMarkdown();
            const cleaned = cleanMarkdown(rawMarkdown);
            if (rawMarkdown !== cleaned) {
              updateContent(cleaned);
              editor.commands.setContent(cleaned, {
                emitUpdate: false,
                contentType: 'markdown',
              });
            }
          }
        }
      };
      const focusinHandler = () => {
        updateIsEditing(true);
        consoleLog('Tiptap focusin detected');
      };
      editorContainer.addEventListener('focusin', focusinHandler);
      editorContainer.addEventListener('focusout', focusoutHandler);
      return () => {
        editorContainer.removeEventListener('focusin', focusinHandler);
        editorContainer.removeEventListener('focusout', focusoutHandler);
      };
    }
  }, [updateIsEditing, editor, updateContent, isRawMarkdown]);

  if (!editor) {
    return null;
  }

  return (
    <div ref={editorContainerRef} className={styles.editorContainer}>
      {/* Formatting Toolbar */}
      {!readOnly && (
        <div className={styles.toolbar}>
          {/* Undo/Redo */}
          <button
            type="button"
            onClick={() => editor.chain().focus().undo().run()}
            disabled={!editor.can().undo() || isRawMarkdown}
            className={styles.toolbarButton}
            title="Undo"
          >
            <svg
              viewBox="0 0 24 24"
              width="16"
              height="16"
              stroke="currentColor"
              strokeWidth="2"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M3 7v6h6" />
              <path d="M21 17a9 9 0 0 0-9-9 9 9 0 0 0-6 2.3L3 13" />
            </svg>
          </button>
          <button
            type="button"
            onClick={() => editor.chain().focus().redo().run()}
            disabled={!editor.can().redo() || isRawMarkdown}
            className={styles.toolbarButton}
            title="Redo"
          >
            <svg
              viewBox="0 0 24 24"
              width="16"
              height="16"
              stroke="currentColor"
              strokeWidth="2"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M21 7v6h-6" />
              <path d="M3 17a9 9 0 0 1 9-9 9 9 0 0 1 6 2.3l3 2.7" />
            </svg>
          </button>

          <div className={styles.toolbarSeparator} />

          {/* Headings */}
          <button
            type="button"
            onClick={() =>
              editor.chain().focus().toggleHeading({ level: 1 }).run()
            }
            className={classNames(styles.toolbarButton, {
              [styles.toolbarButtonActive]: editor.isActive('heading', {
                level: 1,
              }),
            })}
            disabled={isRawMarkdown}
            title="Heading 1"
          >
            <span style={{ fontWeight: 'bold', fontSize: '12px' }}>H1</span>
          </button>
          <button
            type="button"
            onClick={() =>
              editor.chain().focus().toggleHeading({ level: 2 }).run()
            }
            className={classNames(styles.toolbarButton, {
              [styles.toolbarButtonActive]: editor.isActive('heading', {
                level: 2,
              }),
            })}
            disabled={isRawMarkdown}
            title="Heading 2"
          >
            <span style={{ fontWeight: 'bold', fontSize: '12px' }}>H2</span>
          </button>
          <button
            type="button"
            onClick={() =>
              editor.chain().focus().toggleHeading({ level: 3 }).run()
            }
            className={classNames(styles.toolbarButton, {
              [styles.toolbarButtonActive]: editor.isActive('heading', {
                level: 3,
              }),
            })}
            disabled={isRawMarkdown}
            title="Heading 3"
          >
            <span style={{ fontWeight: 'bold', fontSize: '12px' }}>H3</span>
          </button>

          <div className={styles.toolbarSeparator} />

          {/* Bold / Italic */}
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleBold().run()}
            className={classNames(styles.toolbarButton, {
              [styles.toolbarButtonActive]: editor.isActive('bold'),
            })}
            disabled={isRawMarkdown}
            title="Bold"
          >
            <svg
              viewBox="0 0 24 24"
              width="16"
              height="16"
              stroke="currentColor"
              strokeWidth="2.5"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M6 4h8a4 4 0 0 1 4 4 4 4 0 0 1-4 4H6z" />
              <path d="M6 12h9a4 4 0 0 1 4 4 4 4 0 0 1-4 4H6z" />
            </svg>
          </button>
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleItalic().run()}
            className={classNames(styles.toolbarButton, {
              [styles.toolbarButtonActive]: editor.isActive('italic'),
            })}
            disabled={isRawMarkdown}
            title="Italic"
          >
            <svg
              viewBox="0 0 24 24"
              width="16"
              height="16"
              stroke="currentColor"
              strokeWidth="2.5"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="19" y1="4" x2="10" y2="4" />
              <line x1="14" y1="20" x2="5" y2="20" />
              <line x1="15" y1="4" x2="9" y2="20" />
            </svg>
          </button>

          <div className={styles.toolbarSeparator} />

          {/* Lists */}
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            className={classNames(styles.toolbarButton, {
              [styles.toolbarButtonActive]: editor.isActive('bulletList'),
            })}
            disabled={isRawMarkdown}
            title="Bullet List"
          >
            <svg
              viewBox="0 0 24 24"
              width="16"
              height="16"
              stroke="currentColor"
              strokeWidth="2"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="8" y1="6" x2="21" y2="6" />
              <line x1="8" y1="12" x2="21" y2="12" />
              <line x1="8" y1="18" x2="21" y2="18" />
              <line x1="3" y1="6" x2="3.01" y2="6" />
              <line x1="3" y1="12" x2="3.01" y2="12" />
              <line x1="3" y1="18" x2="3.01" y2="18" />
            </svg>
          </button>
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
            className={classNames(styles.toolbarButton, {
              [styles.toolbarButtonActive]: editor.isActive('orderedList'),
            })}
            disabled={isRawMarkdown}
            title="Ordered List"
          >
            <svg
              viewBox="0 0 24 24"
              width="16"
              height="16"
              stroke="currentColor"
              strokeWidth="2"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="10" y1="6" x2="21" y2="6" />
              <line x1="10" y1="12" x2="21" y2="12" />
              <line x1="10" y1="18" x2="21" y2="18" />
              <path d="M4 6H3a1 1 0 0 1-1-1V3a1 1 0 0 1 1-1h1v4z" />
              <path d="M4 10a2 2 0 0 1 2 2 2 2 0 0 1-2 2H2v-2a2 2 0 0 1 2-2z" />
              <path d="M2 18h2a2 2 0 0 1 2 2 2 2 0 0 1-2 2H2" />
            </svg>
          </button>
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleTaskList().run()}
            className={classNames(styles.toolbarButton, {
              [styles.toolbarButtonActive]: editor.isActive('taskList'),
            })}
            disabled={isRawMarkdown}
            title="Checklist"
          >
            <svg
              viewBox="0 0 24 24"
              width="16"
              height="16"
              stroke="currentColor"
              strokeWidth="2"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="9 11 12 14 22 4" />
              <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
            </svg>
          </button>

          <div className={styles.toolbarSeparator} />

          {/* Code Block */}
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleCodeBlock().run()}
            className={classNames(styles.toolbarButton, {
              [styles.toolbarButtonActive]: editor.isActive('codeBlock'),
            })}
            disabled={isRawMarkdown}
            title="Code Block"
          >
            <svg
              viewBox="0 0 24 24"
              width="16"
              height="16"
              stroke="currentColor"
              strokeWidth="2"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="16 18 22 12 16 6" />
              <polyline points="8 6 2 12 8 18" />
            </svg>
          </button>

          <div className={styles.toolbarSeparator} />

          {/* Raw Markdown Toggle */}
          <button
            type="button"
            onClick={() => {
              if (isRawMarkdown) {
                // Sync back to Tiptap editor before closing raw mode
                editor.commands.setContent(value, {
                  emitUpdate: false,
                  contentType: 'markdown',
                });
              }
              setIsRawMarkdown(!isRawMarkdown);
            }}
            className={classNames(styles.toolbarButton, {
              [styles.toolbarButtonActive]: isRawMarkdown,
            })}
            style={{ width: 'auto', padding: '0 6px', gap: '4px' }}
            title="Toggle Raw Markdown Source"
          >
            <span style={{ fontSize: '11px', fontWeight: 'bold' }}>MD</span>
          </button>
        </div>
      )}

      {isRawMarkdown ? (
        <textarea
          value={value}
          onChange={(e) => updateContent(e.target.value)}
          className={styles.rawTextarea}
          placeholder="Write markdown here..."
          disabled={readOnly}
          autoFocus
        />
      ) : (
        <EditorContent
          editor={editor}
          className={classNames(styles.editorArea, 'prose')}
        />
      )}
    </div>
  );
};
